import type { IntervalCounts, Survey, SurveySubmissionPayload } from '@/types/survey';
import { elapsedSeconds } from '@/utils/duration';
import { toLocalIsoString } from '@/utils/format';
import { normalizeCounts } from '@/utils/counts';

/**
 * Thin client for the Cloudflare Worker API.
 *
 * Every call distinguishes three outcomes so the UI can say something true:
 *  - network failure  -> `ApiError.kind === 'network'`  (retry later)
 *  - server rejection -> `ApiError.kind === 'server'`   (retry may still help)
 *  - bad payload      -> `ApiError.kind === 'validation'` (retrying will not help)
 */

export type ApiErrorKind = 'network' | 'server' | 'validation' | 'config';

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status?: number;

  constructor(kind: ApiErrorKind, message: string, status?: number) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
  }

  /** Whether an automatic retry has any chance of succeeding. */
  get retryable(): boolean {
    return this.kind === 'network' || this.kind === 'server';
  }
}

const REQUEST_TIMEOUT_MS = 20_000;

export function apiBaseUrl(): string {
  return (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');
}

export function isApiConfigured(): boolean {
  return apiBaseUrl().length > 0;
}

function headers(): HeadersInit {
  const result: Record<string, string> = { 'Content-Type': 'application/json' };
  const key = import.meta.env.VITE_API_KEY;
  if (key) result['X-API-Key'] = key;
  return result;
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
  const base = apiBaseUrl();
  if (!base) {
    throw new ApiError('config', 'The server address is not configured in this build.');
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(`${base}${path}`, {
      ...init,
      headers: headers(),
      signal: controller.signal,
    });
  } catch (error) {
    const aborted = error instanceof DOMException && error.name === 'AbortError';
    throw new ApiError(
      'network',
      aborted ? 'The server took too long to respond.' : 'Could not reach the server.',
    );
  } finally {
    clearTimeout(timeout);
  }

  let body: unknown = null;
  const text = await response.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = { error: text.slice(0, 200) };
    }
  }

  if (!response.ok) {
    const message =
      (body && typeof body === 'object' && 'error' in body && typeof body.error === 'string'
        ? body.error
        : null) ?? `The server replied with an error (${response.status}).`;
    // 4xx other than 408/429 means the request itself is wrong: do not retry.
    const permanent =
      response.status >= 400 &&
      response.status < 500 &&
      response.status !== 408 &&
      response.status !== 429;
    throw new ApiError(permanent ? 'validation' : 'server', message, response.status);
  }

  return body as T;
}

/**
 * Converts a local Survey record into the API wire format.
 *
 * `intervals` carries the clock-aligned 15 minute breakdown. It is passed in
 * rather than derived here so the caller reads it from the event log once.
 */
export function buildSubmissionPayload(
  survey: Survey,
  intervals: IntervalCounts[] = [],
): SurveySubmissionPayload {
  return {
    id: survey.id,
    surveyorName: survey.surveyorName.trim(),
    surveyDate: survey.surveyDate,
    plannedStartTime: survey.plannedStartTime,
    actualStartAt: toLocalIsoString(survey.actualStartTimestamp),
    actualEndAt: toLocalIsoString(survey.actualEndTimestamp),
    durationSeconds: elapsedSeconds(survey.actualStartTimestamp, survey.actualEndTimestamp),
    location: {
      latitude: survey.latitude ?? null,
      longitude: survey.longitude ?? null,
      accuracy: survey.gpsAccuracy ?? null,
      gpsCapturedAt: toLocalIsoString(survey.gpsCapturedAt),
      district: survey.district ?? '',
      manualLocation: survey.manualLocation ?? '',
    },
    road: {
      roadNumber: survey.roadNumber.trim(),
      mccNumber: survey.mccNumber.trim(),
      weather: survey.weather,
      locationType: survey.locationType,
      roadSurface: survey.roadSurface,
      roadConfiguration: survey.roadConfiguration,
      direction: survey.direction.trim(),
      laneCount: survey.laneCount,
    },
    vehicleCounts: normalizeCounts(survey.vehicleCounts),
    intervals: intervals.map((interval) => ({
      startAt: toLocalIsoString(interval.start) ?? '',
      endAt: toLocalIsoString(interval.end) ?? '',
      counts: normalizeCounts(interval.counts),
      total: interval.total,
    })),
    comments: survey.comments?.trim() ?? '',
    appVersion: survey.appVersion,
    clientCreatedAt: toLocalIsoString(survey.createdAt) ?? '',
  };
}

export interface SubmitResponse {
  ok: true;
  id: string;
  created: boolean;
  totalCount: number;
}

export interface TelegramResponse {
  ok: true;
  id: string;
  sentAt: string;
}

export interface HealthResponse {
  ok: true;
  service: string;
  time: string;
  database: 'up' | 'down';
}

export async function health(): Promise<HealthResponse> {
  return request<HealthResponse>('/api/health', { method: 'GET' });
}

/**
 * Saves the survey to D1. Idempotent — the survey id is the key, so retrying a
 * submission that actually succeeded updates the row instead of duplicating it.
 */
export async function submitSurvey(
  survey: Survey,
  intervals: IntervalCounts[] = [],
): Promise<SubmitResponse> {
  return request<SubmitResponse>('/api/surveys/submit', {
    method: 'POST',
    body: JSON.stringify(buildSubmissionPayload(survey, intervals)),
  });
}

/** Asks the Worker to deliver the Telegram notification for a saved survey. */
export async function sendTelegram(
  survey: Survey,
  intervals: IntervalCounts[] = [],
): Promise<TelegramResponse> {
  return request<TelegramResponse>(`/api/surveys/${encodeURIComponent(survey.id)}/telegram`, {
    method: 'POST',
    body: JSON.stringify({ payload: buildSubmissionPayload(survey, intervals) }),
  });
}

export async function fetchSurvey(id: string): Promise<{ ok: true; survey: unknown }> {
  return request(`/api/surveys/${encodeURIComponent(id)}`, { method: 'GET' });
}
